import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Box, Toolbar, Typography, IconButton, MenuItem, Menu, Button, Avatar, Select, Chip } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import { useFirebase } from './Firebase';
import CryptoJS from 'crypto-js';
import { apiFetch, apiJson, getDataSource, setDataSource, LIVE, DEV } from './api';

const StyledAppBar = styled(AppBar)`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
`

const StyledBox = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
`

const NavBar = () => {
  const { auth, user } = useFirebase();
  const location = useLocation();
  const navigate = useNavigate();
  const [tierListAnchorEl, setTierListAnchorEl] = React.useState(null);
  const [avatarEl, setAvatarEl] = React.useState(null);
  const [pages, setPages] = useState([]);
  const [sv7Pages, setSv7Pages] = useState([]);
  const [profile, setProfile] = useState(null);
  const [needsLink, setNeedsLink] = useState(false);
  const dataSource = getDataSource();
  const tierListOpen = Boolean(tierListAnchorEl);
  const avatarOpen = Boolean(avatarEl);

  // Generate Gravatar URL
  const gravatarUrl = user ? `https://www.gravatar.com/avatar/${CryptoJS.MD5(user.email.trim().toLowerCase())}` : '';

  // Determine current version from URL
  const getVersionFromURL = () => {
    if (location.pathname.startsWith('/sv7/')) return 'sv7';
    if (location.pathname.startsWith('/tier-list/') || location.pathname === '/volforce' || location.pathname.startsWith('/customize')) return 'sv6';
    return null; // neutral page
  };

  // Which versions exist depends on the database being read, so an account
  // with SV7 on live but only SV6 on dev sees the right options in each.
  const active = profile ? (dataSource === DEV ? profile.dev : profile.live) : null;
  const hasSv7Profile = !!(active && active.sv7);
  const devAvailable = !!(profile && profile.dev && profile.dev.available);

  const urlVersion = getVersionFromURL();
  const currentVersion = urlVersion || (hasSv7Profile ? 'sv7' : 'sv6');

  // One call answers which versions and which sources this account can use.
  // A 403 here just means the account has not been linked to a card yet.
  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    apiJson('/api/profile')
      .then((data) => {
        setProfile(data);
        setNeedsLink(false);
      })
      .catch((error) => {
        setProfile(null);
        // The server no longer infers a profile from the Firebase displayName,
        // so an account that has never been linked has to enter its card id
        // and PIN once. Say so rather than just showing empty pages.
        setNeedsLink(error.code === 'NOT_LINKED');
      });
  }, [user]);

  // Fetch sv6 tier pages
  useEffect(() => {
    apiFetch('/api/pages')
      .then((response) => response.json())
      .then((data) => {
        if (data.data.length > 0) {
          setPages(data.data);
        }
      });
  }, []);

  // Fetch sv7 tier pages
  useEffect(() => {
    if (hasSv7Profile) {
      apiFetch('/api/sv7/pages')
        .then(res => res.json())
        .then(data => {
          if (data.data && data.data.length > 0) {
            setSv7Pages(data.data);
          }
        });
    }
  }, [hasSv7Profile]);

  // Switching database reloads the app. Every page fetches in an effect keyed
  // on its own params, so re-rendering alone would leave stale data on screen;
  // a reload guarantees nothing from the other database survives.
  const handleSourceChange = (event) => {
    const next = event.target.value;
    if (next === dataSource) return;
    setDataSource(next);
    window.location.reload();
  };

  // Handle version switch
  const handleVersionChange = (event) => {
    const newVersion = event.target.value;
    if (newVersion === currentVersion) return;

    if (newVersion === 'sv7') {
      if (location.pathname.startsWith('/tier-list/')) {
        navigate('/sv7/tier-list/18');
      } else if (location.pathname === '/volforce') {
        navigate('/sv7/volforce');
      } else if (location.pathname.startsWith('/customize/')) {
        const setting = location.pathname.replace('/customize/', '');
        navigate(`/sv7/customize/${setting}`);
      } else if (location.pathname === '/customize') {
        navigate('/sv7/customize');
      }
    } else {
      if (location.pathname.startsWith('/sv7/tier-list/')) {
        // Default to first available sv6 tier page
        const firstPage = pages.length > 0 ? `${pages[0].difnum}-${pages[0].tier_type}` : '18-s';
        navigate(`/tier-list/${firstPage}`);
      } else if (location.pathname === '/sv7/volforce') {
        navigate('/volforce');
      } else if (location.pathname.startsWith('/sv7/customize/')) {
        const setting = location.pathname.replace('/sv7/customize/', '');
        navigate(`/customize/${setting}`);
      } else if (location.pathname === '/sv7/customize') {
        navigate('/customize');
      }
    }
  };

  const handleTierListMenu = (event) => {
    setTierListAnchorEl(event.currentTarget);
  };

  const closeTierListMenu = () => {
    setTierListAnchorEl(null);
  };

  const handleAvatarMenu = (event) => {
    setAvatarEl(event.currentTarget);
  };

  const handleAvatarClose = () => {
    setAvatarEl(null);
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      alert(error.message);
    }
  };

  // Version-aware route helpers
  const volforceLink = currentVersion === 'sv7' ? '/sv7/volforce' : '/volforce';
  const customizeLink = currentVersion === 'sv7' ? '/sv7/customize' : '/customize';

  return (
    <StyledAppBar position="sticky" sx={{ mb: 6 }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center'}}>
      <StyledBox sx={{justifyContent: 'flex-start'}}>
        <IconButton edge="start" color="inherit" aria-label="logo" component={RouterLink} to="/">
          <img src="/content/logo.png" alt="Logo" style={{width: '40px'}}/>
        </IconButton>
        <Typography variant="h6" component={RouterLink} to="/" style={{ textDecoration: 'none', color: 'white' }}>
          Mitsuru
        </Typography>
        <Box borderLeft={1} borderColor="divider" sx={{ mx: 1 }} />
        <Button variant="text" color="inherit" onClick={handleTierListMenu} sx={{ mx: 0.5 }}>
          Tiers
        </Button>
        <Button variant="text" color="inherit" component={RouterLink} to={volforceLink} sx={{ mx: 0.5 }}>
          Volforce
        </Button>
        <Button variant="text" color="inherit" component={RouterLink} to={customizeLink} sx={{ mx: 0.5 }}>
          Customize
        </Button>
        <Menu
          id="menu-appbar"
          anchorEl={tierListAnchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          keepMounted
          open={tierListOpen}
          onClose={closeTierListMenu}
          sx={{ mt: 1 }}
        >
        {currentVersion === 'sv7' ? (
          sv7Pages.map(({ level }) => (
            <MenuItem key={`sv7-${level}`} onClick={closeTierListMenu} component={RouterLink} to={`/sv7/tier-list/${level}`}>
              <Typography component="span" sx={{ fontWeight: 'bold' }}>Lv. {level}</Typography>
            </MenuItem>
          ))
        ) : (
          pages.map(({ difnum, tier_type }) => (
            <MenuItem key={`${difnum}-${tier_type}`} onClick={closeTierListMenu} component={RouterLink} to={`/tier-list/${difnum}-${tier_type}`}>
              <Typography component="span" sx={{ fontWeight: 'bold', mr:0.5 }}>{difnum}</Typography>
              {`${tier_type.toUpperCase()}`}
            </MenuItem>
          ))
        )}
        </Menu>
      </StyledBox>
      {user && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {hasSv7Profile && (
            <Select
              value={currentVersion}
              onChange={handleVersionChange}
              size="small"
              variant="outlined"
              sx={{
                mr: 1.5,
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
                '.MuiSvgIcon-root': { color: 'white' },
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              <MenuItem value="sv7">SV7</MenuItem>
              <MenuItem value="sv6">SV6</MenuItem>
            </Select>
          )}
          {devAvailable && (
            <Select
              value={dataSource}
              onChange={handleSourceChange}
              size="small"
              variant="outlined"
              sx={{
                mr: 1.5,
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: dataSource === DEV ? '#ffb300' : 'rgba(255,255,255,0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: dataSource === DEV ? '#ffc947' : 'rgba(255,255,255,0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
                '.MuiSvgIcon-root': { color: 'white' },
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              <MenuItem value={LIVE}>Live</MenuItem>
              <MenuItem value={DEV}>Dev</MenuItem>
            </Select>
          )}
          {needsLink && (
            <Chip
              label="Link your card in Settings"
              size="small"
              component={RouterLink}
              to="/user-settings"
              clickable
              sx={{
                mr: 1.5,
                fontWeight: 'bold',
                bgcolor: '#d32f2f',
                color: 'white',
                '&:hover': { bgcolor: '#e53935' },
              }}
            />
          )}
          {dataSource === DEV && (
            // Customization writes go to whichever database is selected, so it
            // needs to be obvious at a glance which one that is.
            <Chip
              label="DEV DATA"
              size="small"
              sx={{
                mr: 1.5,
                fontWeight: 'bold',
                letterSpacing: '0.05em',
                bgcolor: '#ffb300',
                color: '#1a1a1a',
              }}
            />
          )}
          <IconButton onClick={handleAvatarMenu} size="small">
            <Avatar alt={user.email} src={gravatarUrl} />
          </IconButton>
          <Menu
            id="avatar-menu"
            anchorEl={avatarEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            open={avatarOpen}
            onClose={handleAvatarClose}
          >
            <MenuItem onClick={handleAvatarClose} component={RouterLink} to="/user-settings">Settings</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default NavBar;
